<template>
  <div class="modal fade" :id="dialogId" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-fullscreen-md-down" role="document">
      <div class="modal-content">
        <button type="button" class="dialog-close-btn" data-bs-dismiss="modal" :aria-label="$t('shared.close')">
          <span class="material-icons">close</span>
        </button>
        <div class="modal-body p-0">

          <!-- ── MAIN VIEW ── (v-show keeps Bootstrap tab state and indicator position alive) -->
          <div v-show="currentView === 'main'">
            <div class="container">
              <!-- Tabs -->
              <template v-if="!printMapMode && !mapsOnly">
                <ul class="nav bg-gray mt-3 mb-1 position-relative rounded-pill p-1 map-styles-tabs" :id="tabsId" role="tablist">
                  <div class="nav-indicator"></div>
                  <li role="presentation">
                    <button
                      class="nav-link rounded-pill active"
                      :id="mapsTabId"
                      data-bs-toggle="pill"
                      :data-bs-target="`#${mapsPaneId}`"
                      type="button" role="tab"
                      :aria-controls="mapsPaneId"
                      aria-selected="true"
                    >{{ $t('shared.maps') }}</button>
                  </li>
                  <li role="presentation">
                    <button
                      class="nav-link rounded-pill"
                      :id="overlaysTabId"
                      data-bs-toggle="pill"
                      :data-bs-target="`#${overlaysPaneId}`"
                      type="button" role="tab"
                      :aria-controls="overlaysPaneId"
                      aria-selected="false"
                    >{{ $t('base_map_dialog.base_map_styles_dialog.overlays') }}</button>
                  </li>
                  <li role="presentation">
                    <button
                      class="nav-link rounded-pill"
                      :id="poisTabId"
                      data-bs-toggle="pill"
                      :data-bs-target="`#${poisPaneId}`"
                      type="button" role="tab"
                      :aria-controls="poisPaneId"
                      aria-selected="false"
                    >{{ $t('base_map_dialog.base_map_styles_dialog.pois') }}</button>
                  </li>
                </ul>
              </template>

              <!-- Tab content -->
              <div class="tab-content" id="pills-tabContent">
                <!-- Maps Tab -->
                <div class="tab-pane fade show active" :id="mapsPaneId" role="tabpanel" :aria-labelledby="mapsTabId">
                  <div class="row">
                    <!-- Free Styles -->
                    <div class="col-12 px-0 pt-3 pb-2">
                      <p class="px-3 mb-0"><small><strong>{{ $t('base_map_dialog.base_map_styles_dialog.standard_maps') }}</strong></small></p>
                      <div ref="freeScroll" class="mapstyle-list">
                        <div
                          class="position-relative d-inline-block"
                          v-for="(style, index) in freeMapStyles"
                          :key="index"
                          @click="onStyleClick(style)"
                        >
                          <div class="mapstyle-card" :class="{ 'current-style': currentMapStyle && style.name === currentMapStyle.name }">
                            <img :src="style.image" :alt="$t('shared.map')" draggable="false" />
                            <div class="hiking-text p-1">
                              <small><p class="m-0"><strong>{{ style.displayName() }}</strong></p></small>
                              <small><p class="text-muted">{{ style.isPro ? 'PRO+' : $t('base_map_dialog.base_map_styles_dialog.free') }}</p></small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- PRO Styles -->
                    <div class="col-12 px-0 py-1 d-block">
                      <div class="px-3 mb-0 d-flex align-items-center justify-content-between">
                        <small><strong>{{ $t('base_map_dialog.base_map_styles_dialog.pro_map_collections') }}</strong></small>
                        <a
                          class="collections-info"
                          href="https://blog.hiiker.app/2024/03/09/introducing-map-collections/"
                          target="_blank"
                          rel="noopener"
                          :title="$t('base_map_dialog.base_map_styles_dialog.about_map_collections')"
                          @click.stop
                        >
                          <span class="material-icons">info</span>
                        </a>
                      </div>
                      <div ref="proScroll" class="mapstyle-list">
                        <div
                          class="position-relative d-inline-block"
                          v-for="(style, index) in proMapStyles"
                          :key="index"
                          @click="onStyleClick(style)"
                        >
                          <div :class="['mapstyle-card', { 'cannot-print': printMapMode && !style.isPrintMap, 'current-style': currentMapStyle && style.name === currentMapStyle.name }]">
                            <img :src="style.image" :alt="$t('shared.map')" draggable="false" />
                            <div class="hiking-text p-1">
                              <small><p class="m-0"><strong>{{ style.displayName() }}</strong></p></small>
                              <small><p class="text-muted">{{ style.isPro ? 'PRO+' : $t('base_map_dialog.base_map_styles_dialog.free') }}</p></small>
                            </div>
                            <button
                              v-if="showsScaleLockBadge(style)"
                              type="button"
                              class="scale-lock-badge"
                              :title="$t('base_map_dialog.base_map_styles_dialog.locked_to_the_1_50k_series')"
                              @click.stop="openMapSettings"
                            >1:50k</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Inline 3D Mode toggle + More button -->
                    <div class="col-12 px-3 pt-3 pb-2">
                      <div class="footer-actions footer-actions--inline">
                        <div v-if="show3DToggle" class="footer-action" @click.stop>
                          <div class="toggle-pill" @click="onClickToggle3D(!is3DEnabled)">
                            <span :class="['toggle-pill__option', { active: !is3DEnabled }]">{{ $t('base_map_dialog.base_map_styles_dialog.off') }}</span>
                            <span :class="['toggle-pill__option', { active: is3DEnabled }]">{{ $t('base_map_dialog.base_map_styles_dialog.text_90651ebe') }}</span>
                          </div>
                          <div class="footer-action__label">{{ $t('base_map_dialog.base_map_styles_dialog.3d_mode') }}</div>
                        </div>
                        <div v-if="!printMapMode && !mapsOnly" class="footer-action" @click="currentView = 'more'">
                          <div class="more-circle-btn">
                            <span class="material-icons">more_vert</span>
                          </div>
                          <div class="footer-action__label">{{ $t('base_map_dialog.base_map_styles_dialog.more') }}</div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <template v-if="!printMapMode && !mapsOnly">
                  <!-- Overlays Tab -->
                  <div class="tab-pane fade" :id="overlaysPaneId" role="tabpanel" :aria-labelledby="overlaysTabId">
                    <BaseMapDataTrailsTab @customise-overlay-line="openOverlayLineStyle" @open-hike-lists="openHikeLists" />
                  </div>
                  <!-- POIs Tab -->
                  <div class="tab-pane fade" :id="poisPaneId" role="tabpanel" :aria-labelledby="poisTabId">
                    <BaseMapDataPoIsTab />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- ── MORE VIEW ── -->
          <template v-if="currentView === 'more'">
            <div class="sub-view p-3">
              <button class="btn btn-link text-white p-0 mb-3" @click="currentView = 'main'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-2"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.more_2') }}</strong></h5>

              <!-- Featured options (yellow icon) -->
              <div class="more-option" @click="onPrintMapFromMore">
                <div class="more-option-icon more-option-icon--featured">
                  <span class="material-icons">print</span>
                </div>
                <div class="more-option-text">
                  <div><strong>{{ $t('base_map_dialog.base_map_styles_dialog.print_a_map') }}</strong></div>
                  <small class="text-muted">{{ $t('base_map_dialog.base_map_styles_dialog.print_an_area_of_map') }}</small>
                </div>
                <span class="material-icons more-chevron">chevron_right</span>
              </div>

              <div class="more-option" @click="currentView = 'legend'">
                <div class="more-option-icon more-option-icon--featured">
                  <span class="material-icons">list</span>
                </div>
                <div class="more-option-text">
                  <div><strong>{{ $t('base_map_dialog.base_map_styles_dialog.map_legends') }}</strong></div>
                  <small class="text-muted">{{ $t('base_map_dialog.base_map_styles_dialog.information_on_the_map_icons') }}</small>
                </div>
                <span class="material-icons more-chevron">chevron_right</span>
              </div>

              <div class="more-option" @click="currentView = 'compareSelect'">
                <div class="more-option-icon more-option-icon--featured">
                  <span class="material-icons">compare</span>
                </div>
                <div class="more-option-text">
                  <div><strong>{{ $t('base_map_dialog.base_map_styles_dialog.compare_maps') }}</strong></div>
                  <small class="text-muted">{{ $t('base_map_dialog.base_map_styles_dialog.compare_and_contrast_two_maps') }}</small>
                </div>
                <span class="material-icons more-chevron">chevron_right</span>
              </div>

              <!-- Secondary options (gray icon) -->
              <div class="more-option" @click="currentView = 'routeStyle'">
                <div class="more-option-icon">
                  <span class="material-icons">edit</span>
                </div>
                <div class="more-option-text flex-grow-1">{{ $t('base_map_dialog.base_map_styles_dialog.customise_route_style') }}</div>
                <span class="material-icons more-chevron">chevron_right</span>
              </div>

              <div class="more-option" @click="currentView = 'mapSettings'">
                <div class="more-option-icon">
                  <span class="material-icons">settings</span>
                </div>
                <div class="more-option-text flex-grow-1">{{ $t('base_map_dialog.base_map_styles_dialog.map_settings') }}</div>
                <span class="material-icons more-chevron">chevron_right</span>
              </div>
            </div>
          </template>

          <!-- ── LEGEND VIEW ── -->
          <template v-if="currentView === 'legend'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'more'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.map_legends_2') }}</strong></h5>
            </div>
            <MapLegendsTab />
          </template>

          <!-- ── ROUTE STYLE VIEW ── -->
          <template v-if="currentView === 'routeStyle'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e; margin-top: 28px;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'more'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.customise_route_style_2') }}</strong></h5>
              <a
                class="btn btn-link text-white p-0 ms-auto"
                href="https://support.hiiker.app/article/customising-your-route-line-style-on-hiiker/"
                target="_blank"
                rel="noopener"
                :title="$t('base_map_dialog.base_map_styles_dialog.learn_more_about_customising_route_style')"
              >
                <span class="material-icons">info</span>
              </a>
            </div>
            <CustomiseRouteStyleView />
          </template>

          <!-- ── OVERLAY LINE STYLE VIEW ── (one overlay, opened from its card's gear) -->
          <template v-if="currentView === 'overlayLineStyle' && overlayLineStyleType">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e; margin-top: 28px;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'main'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ overlayLineStyleTitle }}</strong></h5>
              <a
                class="btn btn-link text-white p-0 ms-auto"
                href="https://support.hiiker.app/article/customising-your-route-line-style-on-hiiker/"
                target="_blank"
                rel="noopener"
                :title="$t('base_map_dialog.base_map_styles_dialog.learn_more_about_customising_line_style')"
              >
                <span class="material-icons">info</span>
              </a>
            </div>
            <CustomiseOverlayLineStyleView :overlay-type="overlayLineStyleType" />
          </template>

          <!-- ── HIKE LISTS VIEW ── (opened from the cog on the Hike lists card) -->
          <template v-if="currentView === 'hikeLists'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e; margin-top: 28px;">
              <button class="btn btn-link text-white p-0" :aria-label="$t('shared.back')" @click="currentView = 'main'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.hike_lists') }}</strong></h5>
            </div>
            <HikeListOverlayView @edit-style="openHikeListStyle" />
          </template>

          <!-- ── HIKE LIST LINE STYLE VIEW ── (one list, opened from its row's cog) -->
          <template v-if="currentView === 'hikeListStyle' && hikeListStyleId">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e; margin-top: 28px;">
              <button class="btn btn-link text-white p-0" :aria-label="$t('shared.back')" @click="currentView = 'hikeLists'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ hikeListStyleTitle }}</strong></h5>
              <a
                class="btn btn-link text-white p-0 ms-auto"
                href="https://support.hiiker.app/article/customising-your-route-line-style-on-hiiker/"
                target="_blank"
                rel="noopener"
                :title="$t('base_map_dialog.base_map_styles_dialog.learn_more_about_customising_line_style')"
              >
                <span class="material-icons">info</span>
              </a>
            </div>
            <CustomiseHikeListLineStyleView :list-id="hikeListStyleId" />
          </template>

          <!-- ── MAP SETTINGS VIEW ── -->
          <template v-if="currentView === 'mapSettings'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'more'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.map_settings_2') }}</strong></h5>
            </div>
            <MapSettingsView :mapInstanceName="mapInstanceName" @navigate="currentView = $event" />
          </template>

          <!-- ── MAP PROJECTION VIEW ── -->
          <template v-if="currentView === 'mapProjection'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'mapSettings'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.map_projection') }}</strong></h5>
            </div>
            <MapProjectionView :mapInstanceName="mapInstanceName" />
          </template>

          <!-- ── MAP ELEVATION VIEW ── -->
          <template v-if="currentView === 'mapElevation'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e; margin-top: 28px;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'mapSettings'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.3d_elevation_exaggeration') }}</strong></h5>
              <a
                class="btn btn-link text-white p-0 ms-auto"
                href="https://support.hiiker.app/faq/what-is-elevation-exaggeration/"
                target="_blank"
                rel="noopener"
                :title="$t('base_map_dialog.base_map_styles_dialog.what_is_elevation_exaggeration')"
              >
                <span class="material-icons">info</span>
              </a>
            </div>
            <MapElevationView :mapInstanceName="mapInstanceName" />
          </template>

          <!-- ── COMPARE MAPS SELECT VIEW ── -->
          <template v-if="currentView === 'compareSelect'">
            <div class="d-flex align-items-center gap-3 p-3" style="border-bottom: 1px solid #2e2e2e;">
              <button class="btn btn-link text-white p-0" @click="currentView = 'more'">
                <span class="material-icons">arrow_back</span>
              </button>
              <h5 class="mb-0"><strong>{{ $t('base_map_dialog.base_map_styles_dialog.compare_maps_2') }}</strong></h5>
            </div>
            <CompareMapsSelectorView :currentMapStyle="currentMapStyle" @select="onCompareStyleSelected" />
          </template>

        </div>

        <!-- Compare Maps fullscreen overlay (teleported to body) -->
        <CompareMapsOverlay
          :active="compareActive"
          :styleUrl="compareStyleUrl"
          :styleName="compareStyleName"
          :baseStyleUrl="currentMapStyle ? currentMapStyle.styleUrl : ''"
          :mapInstanceName="mapInstanceName"
          @exit="compareActive = false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, onBeforeUnmount } from 'vue';
import { MapsSheetTab, SHOW_MAPS_SHEET_TAB_EVENT } from '../../utils/onThisMap';
import { Tab, Modal } from 'bootstrap';
import { HiikerMapStyle, ThreeDimensionalMapManager, isNationalStyleSlug } from '../../utils/models/MapStyles';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import axiosClient from '../../utils/helpers/axios_client';
import emitter from '../../utils/bus';
import BaseMapDataPoIsTab from "./components/BaseMapDataPOIsTab.vue";
import BaseMapDataOverlaysTab from "./components/BaseMapDataOverlaysTab.vue";
import MapLegendsTab from "./components/MapLegendsTab.vue";
import CustomiseRouteStyleView from "./components/CustomiseRouteStyleView.vue";
import CustomiseOverlayLineStyleView from "./components/CustomiseOverlayLineStyleView.vue";
import HikeListOverlayView from "./components/HikeListOverlayView.vue";
import CustomiseHikeListLineStyleView from "./components/CustomiseHikeListLineStyleView.vue";
import MapSettingsView from "./components/MapSettingsView.vue";
import MapProjectionView from "./components/MapProjectionView.vue";
import MapElevationView from "./components/MapElevationView.vue";
import CompareMapsSelectorView from "./components/CompareMapsSelectorView.vue";
import CompareMapsOverlay from "./components/CompareMapsOverlay.vue";
import { getMapSettings, applyMapSettings } from '../../utils/mapSettings';
import { OVERLAY_LINE_LABELS, OverlayLineType } from '../../utils/overlayLineStyles';

export default defineComponent({
  components: { BaseMapDataTrailsTab: BaseMapDataOverlaysTab, BaseMapDataPoIsTab, MapLegendsTab, CustomiseRouteStyleView, CustomiseOverlayLineStyleView, HikeListOverlayView, CustomiseHikeListLineStyleView, MapSettingsView, MapProjectionView, MapElevationView, CompareMapsSelectorView, CompareMapsOverlay },
  props: {
    dialogId: { type: String, default: 'map-styles-dialog' },
    mapInstanceName: { type: String, default: 'map' },
    mapsOnly: { type: Boolean, default: false },
    show3DToggle: { type: Boolean, default: true },
    // Force a full style reload (diff: false) on style change. Mapbox's diff
    // mode silently drops custom sources/layers AND skips the `style.load`
    // event, so consumers that re-add their layers on `style.load` (e.g. the
    // 3D flyover preview's route line) never get to re-render. Opt in to make
    // the event fire reliably; the default keeps diff mode for the main map.
    forceFullStyleReload: { type: Boolean, default: false }
  },

  setup() {
    const freeScroll = ref<HTMLElement | null>(null);
    const proScroll = ref<HTMLElement | null>(null);

    const makeDraggable = (el: HTMLElement) => {
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let moved = false;

      el.addEventListener('mousedown', e => {
        isDown = true; moved = false;
        el.classList.add('dragging');
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
      });
      el.addEventListener('mousemove', e => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX) * 1.5;
        if (Math.abs(x - startX) > 5) moved = true;
        el.scrollLeft = scrollLeft - walk;
      });
      ['mouseup', 'mouseleave'].forEach(evt =>
        el.addEventListener(evt, () => { isDown = false; el.classList.remove('dragging'); })
      );
      el.addEventListener('click', e => { if (moved) e.stopImmediatePropagation(); }, true);
    };

    nextTick(() => {
      if (freeScroll.value) makeDraggable(freeScroll.value);
      if (proScroll.value) makeDraggable(proScroll.value);
    });

    const nav = ref<HTMLElement | null>(null);
    const indicator = ref<HTMLElement | null>(null);

    const moveIndicator = (el: HTMLElement) => {
      if (!nav.value || !indicator.value || !el) return;
      const navRect = nav.value.getBoundingClientRect();
      const btnRect = el.getBoundingClientRect();
      indicator.value.style.width = `${btnRect.width}px`;
      indicator.value.style.height = `${btnRect.height}px`;
      indicator.value.style.left = `${btnRect.left - navRect.left}px`;
      indicator.value.style.top = `${btnRect.top - navRect.top}px`;
    };

    const setActiveIndicator = () => {
      if (!nav.value) return;
      const activeTab = nav.value.querySelector<HTMLButtonElement>('.nav-link.active');
      if (activeTab) moveIndicator(activeTab);
    };

    const handleResize = () => setActiveIndicator();
    window.addEventListener('resize', handleResize);
    onBeforeUnmount(() => window.removeEventListener('resize', handleResize));

    return { freeScroll, proScroll, nav, indicator, moveIndicator, setActiveIndicator };
  },

  data() {
    return {
      mapStyles: [] as HiikerMapStyle[],
      is3DEnabled: ThreeDimensionalMapManager.is3DModeEnabled(),
      isInitialized: false,
      currentView: 'main' as 'main' | 'more' | 'legend' | 'routeStyle' | 'overlayLineStyle' | 'hikeLists' | 'hikeListStyle' | 'mapSettings' | 'mapProjection' | 'mapElevation' | 'compareSelect',
      // Which overlay the line-style sub-view is editing. Set by the gear on an
      // overlay card; null whenever that view isn't open.
      overlayLineStyleType: null as OverlayLineType | null,
      // Which hike list the hike-list line-style sub-view is editing.
      hikeListStyleId: null as string | null,
      compareActive: false,
      compareStyleUrl: '',
      compareStyleName: '',
      nationalScaleLocked: getMapSettings().lockNationalTo50k,
    };
  },

  async beforeCreate() {
    emitter.on('user:updated', async () => {
      const response = await axiosClient.get('/user/current');
      if (response.data) {
        this.user = response.data;
        window.user = this.user;
      }
    });
  },

  async mounted() {
    this.mapStyles = HiikerMapStyle.getAllStyles();
    const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
    const current = HiikerMapStyle.getCurrentStyle(isProOrProPlus);
    if (current) this.$store.commit('mapStyles/setCurrentMapStyle', current);

    nextTick(() => {
      this.nav = document.getElementById(this.tabsId) as HTMLElement | null;
      this.indicator = this.nav?.querySelector<HTMLDivElement>('.nav-indicator') || null;

      const modalEl = document.getElementById(this.dialogId);
      if (!modalEl) return;

      modalEl.addEventListener('shown.bs.modal', () => {
        if (this.mapsOnly || this.printMapMode) return;
        // A tab chosen while the dialog was hidden (showOnTab) couldn't place the
        // indicator then — nothing had a size. Place it now.
        if (this.isInitialized) { this.setActiveIndicator(); return; }
        const firstBtn = document.getElementById(this.mapsTabId);
        if (!firstBtn) return;
        Tab.getOrCreateInstance(firstBtn).show();
        this.moveIndicator(firstBtn);
        this.isInitialized = true;
      });

      modalEl.addEventListener('hidden.bs.modal', () => {
        this.currentView = 'main';
      });

      this.nav?.addEventListener('shown.bs.tab', (e: Event) => {
        this.moveIndicator(e.target as HTMLElement);
      });

      if (!this.mapsOnly) this.setActiveIndicator();
    });

    emitter.on('map:3d-state-changed', this.handleExternal3DState);
    emitter.on('map-loaded', this.applyCurrentMapSettings);
    emitter.on('map-style-loaded', this.applyCurrentMapSettings);
    emitter.on('map-settings-changed', this.refreshMapSettingsState);
    emitter.on(SHOW_MAPS_SHEET_TAB_EVENT, this.handleShowTab);
  },

  methods: {
    applyCurrentMapSettings() {
      const map = this.getMapInstance();
      if (map) applyMapSettings(map, getMapSettings());
      this.refreshMapSettingsState();
    },
    refreshMapSettingsState() {
      this.nationalScaleLocked = getMapSettings().lockNationalTo50k;
    },
    /** The 1:50k lock only applies to National+, so only its card is badged. */
    showsScaleLockBadge(style: HiikerMapStyle): boolean {
      return this.nationalScaleLocked && isNationalStyleSlug(style?.toString?.());
    },
    /**
     * The badge is the shortest route back to the switch that set it — without
     * this you'd have to remember it lives behind More → Map settings.
     */
    openMapSettings() {
      this.currentView = 'mapSettings';
    },
    getMapInstance() {
      return (window as Record<string, any>)[this.mapInstanceName];
    },
    /**
     * Opens the dialog straight on one of its tabs — e.g. from the main map's "On this
     * map" tray, so a tap on its Overlays row lands on Overlays rather than Maps.
     */
    // The bus route to showOnTab, for callers that don't hold a ref to this dialog (the
    // "On this map" tray). Only the addressed dialog answers; by default the main one.
    handleShowTab(payload?: { tab?: MapsSheetTab; dialogId?: string }) {
      if (!payload?.tab || (payload.dialogId ?? 'map-styles-dialog') !== this.dialogId) return;
      this.showOnTab(payload.tab);
    },
    showOnTab(tab: MapsSheetTab) {
      const modalEl = document.getElementById(this.dialogId);
      if (!modalEl) return;
      const tabIds = { maps: this.mapsTabId, overlays: this.overlaysTabId, pois: this.poisTabId };
      const tabBtn = this.mapsOnly || this.printMapMode ? null : document.getElementById(tabIds[tab]);
      if (tabBtn) {
        this.currentView = 'main';
        Tab.getOrCreateInstance(tabBtn).show();
        // Otherwise the first-open default would switch back to Maps on `shown`.
        this.isInitialized = true;
      }
      Modal.getOrCreateInstance(modalEl).show();
    },
    user() {
      return window.user?.firebase_user_id ? window.user : null;
    },
    handleExternal3DState(enabled: boolean) {
      this.is3DEnabled = enabled;
    },
    set3DMode(enabled: boolean) {
      if (this.mapInstanceName === 'map') {
        ThreeDimensionalMapManager.set3DMode(enabled);
        this.is3DEnabled = ThreeDimensionalMapManager.is3DModeEnabled();
        return;
      }
      const mapInstance = this.getMapInstance();
      if (!mapInstance) return;
      if (enabled) {
        if (mapInstance.getSource('mapbox-dem') == null) {
          mapInstance.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14
          });
        }
        if (mapInstance.getTerrain() == null) {
          mapInstance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.0 });
        }
        mapInstance.setPitch(50);
      } else {
        if (mapInstance.getTerrain()) mapInstance.setTerrain(null);
        mapInstance.setPitch(0);
      }
      this.is3DEnabled = enabled;
    },
    async onClickToggle3D(enabled: boolean) {
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }
      this.set3DMode(enabled);
    },
    openPrintMap() {
      const mapInstance = this.getMapInstance();
      let url = '/print-maps';
      if (mapInstance) {
        try {
          const center = mapInstance.getCenter();
          const zoom = mapInstance.getZoom();
          const params = new URLSearchParams({
            lat: center.lat.toFixed(6),
            lng: center.lng.toFixed(6),
            zoom: zoom.toFixed(2)
          });
          url = `/print-maps?${params.toString()}`;
        } catch (e) {}
      }
      window.location.href = url;
    },
    /** Opens the single-overlay line-style editor for the gear that was tapped. */
    openOverlayLineStyle(type: OverlayLineType) {
      this.overlayLineStyleType = type;
      this.currentView = 'overlayLineStyle';
    },
    /** Opens the Hike lists picker from the cog on its overlay card. */
    openHikeLists() {
      this.currentView = 'hikeLists';
    },
    /** Opens one hike list's line style from the edit icon on its row. */
    openHikeListStyle(listId: string) {
      this.hikeListStyleId = listId;
      this.currentView = 'hikeListStyle';
    },
    goBack() {
      const parent: Record<string, string> = {
        more: 'main',
        legend: 'more',
        routeStyle: 'more',
        overlayLineStyle: 'main',
        hikeLists: 'main',
        hikeListStyle: 'hikeLists',
        mapSettings: 'more',
        mapProjection: 'mapSettings',
        mapElevation: 'mapSettings',
        compareSelect: 'more',
      };
      this.currentView = (parent[this.currentView] ?? 'main') as any;
    },
    onCompareStyleSelected(style: any) {
      this.compareStyleUrl = style.styleUrl;
      this.compareStyleName = style.name;
      this.compareActive = true;
      const modalEl = document.getElementById(this.dialogId);
      if (modalEl) Modal.getInstance(modalEl)?.hide();
    },
    onPrintMapFromMore() {
      this.openPrintMap();
    },
    async onStyleClick(style: HiikerMapStyle) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      const mapInstance = this.getMapInstance();
      if (this.printMapMode && !style.isPrintMap) {
        alert(this.$t('base_map_dialog.base_map_styles_dialog.not_available_for_printing'));
        return;
      }
      if (!mapInstance) return;
      if (!style.isPro || (window.user && isProOrProPlus)) {
        this.$store.commit('mapStyles/setCurrentMapStyle', style);
        if (style.canBeSaved()) style.saveCurrentStyle();
        const was3DEnabled = this.show3DToggle && this.is3DEnabled;
        mapInstance.once('style.load', () => {
          if (was3DEnabled) this.set3DMode(true);
          emitter.emit('map-style-loaded');
        });
        const url = this.$store.state.mapStyles.printMapMode ? style.printMapUrl() : style.styleUrl;
        mapInstance.setStyle(url, this.forceFullStyleReload ? { diff: false } : undefined);
        setTimeout(() => emitter.emit('map-style-loaded'), 2500);
      } else {
        window.location.href = '/purchases';
      }
    }
  },

  computed: {
    currentMapStyle() { return this.$store.state.mapStyles.currentMapStyle; },
    overlayLineStyleTitle(): string {
      const type = this.overlayLineStyleType;
      return type
        ? this.$t('base_map_dialog.base_map_styles_dialog.named_line_style', { label: OVERLAY_LINE_LABELS[type] })
        : this.$t('base_map_dialog.base_map_styles_dialog.line_style');
    },
    hikeListStyleTitle(): string {
      const lists = this.$store.state.mapDataOverlays?.hikeLists ?? [];
      const list = lists.find((l: any) => String(l.id) === this.hikeListStyleId);
      return list
        ? this.$t('base_map_dialog.base_map_styles_dialog.named_line_style', { label: list.name })
        : this.$t('base_map_dialog.base_map_styles_dialog.line_style');
    },
    tabsId() { return `${this.dialogId}-tabs`; },
    mapsTabId() { return `${this.dialogId}-maps-tab`; },
    overlaysTabId() { return `${this.dialogId}-overlays-tab`; },
    poisTabId() { return `${this.dialogId}-pois-tab`; },
    mapsPaneId() { return `${this.dialogId}-maps-pane`; },
    overlaysPaneId() { return `${this.dialogId}-overlays-pane`; },
    poisPaneId() { return `${this.dialogId}-pois-pane`; },
    freeMapStyles() { return this.mapStyles.filter(s => !s.isPro); },
    proMapStyles() { return this.mapStyles.filter(s => s.isPro); },
    printMapMode() { return this.$store.state.mapStyles.printMapMode; }
  },

  beforeUnmount() {
    emitter.off('map:3d-state-changed', this.handleExternal3DState);
    emitter.off('map-loaded', this.applyCurrentMapSettings);
    emitter.off('map-style-loaded', this.applyCurrentMapSettings);
    emitter.off('map-settings-changed', this.refreshMapSettingsState);
    emitter.off(SHOW_MAPS_SHEET_TAB_EVENT, this.handleShowTab);
  }
});
</script>

<style scoped>
@media (min-width: 768px) {
  .modal-dialog {
    height: 77%;
    max-height: 77vh;
  }
  .modal-content {
    height: 100%;
  }
}
.modal-content {
  display: flex;
  flex-direction: column;
  position: relative;
}
.modal-body {
  overflow-y: auto;
  flex: 1;
}
.modal-footer {
  border-top: 1px solid #434343;
  justify-content: center;
}

/* ── Dialog close button (top-right X) ── */
.dialog-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #fff;
  line-height: 1;
}
.dialog-close-btn .material-icons {
  font-size: 22px;
  color: #fff;
}

/* ── Footer action buttons ── */
.footer-actions {
  display: flex;
  gap: 32px;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  padding: 4px 0;
}
.footer-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.footer-action__label {
  font-size: 12px;
  color: #ccc;
  font-weight: 500;
}

/* ── Compact 3D Mode toggle ── */
.toggle-pill {
  display: flex;
  align-items: center;
  background: var(--gray) ;
  border-radius: 99px;
  padding: 4px;
  height: 52px;
}
/* A 44px circle for the short labels most languages use, growing into a pill for longer ones --
   a fixed width let Portuguese DESLIGADO/LIGADO spill out and overlap each other. 5px keeps every
   current 2-3 letter label (widest: NON, 34px of text) an exact circle. */
.toggle-pill__option {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  padding: 0 5px;
  border-radius: 99px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  color: #888;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.toggle-pill__option.active {
  background: #fddf00;
  color: #17181d;
}

/* ── More circle button ── */
.more-circle-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--gray) ;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: opacity 0.2s;
}
.footer-action:hover .more-circle-btn { opacity: 0.75; }

/* ── Tabs ── */
.map-styles-tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  max-width: 380px;
  margin: auto;
}
.map-styles-tabs > li { flex: 1 1 0; display: flex; }
.map-styles-tabs .nav-link {
  flex: 1 1 0;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  font-weight: 600;
}
.map-styles-tabs .nav-link.active { color: var(--dark); }
.nav-indicator {
  position: absolute;
  top: 0; left: 0;
  width: 0; height: 0;
  background-color: #fddf00;
  border-radius: 9999px;
  transition: left 0.3s ease, width 0.3s ease, top 0.3s ease, height 0.3s ease;
  z-index: 0;
}

/* ── Map style cards ── */
.mapstyle-list {
  display: flex;
  overflow-x: auto;
  padding-bottom: 1em;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
}
.mapstyle-list::-webkit-scrollbar { display: none; }
.mapstyle-list.dragging { cursor: grabbing; }
.mapstyle-list > .position-relative { flex: 0 0 auto; margin-right: 0; }
.mapstyle-list > .position-relative:first-child { margin-left: 1.5em; }

.mapstyle-card {
  position: relative;
  width: 120px;
  background-color: #fff;
  border: 2px solid #434343;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  margin-top: 1em;
  margin-right: 1em;
}
.mapstyle-card img { width: 100%; height: auto; }
.mapstyle-card .hiking-text {
  position: absolute;
  bottom: 0; left: 0;
  width: 100%;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.7);
}
.collections-info {
  display: flex;
  align-items: center;
  color: #999;
  line-height: 1;
}
.collections-info:hover { color: #fff; }
.collections-info .material-icons { font-size: 18px; }

.mapstyle-card .scale-lock-badge {
  position: absolute;
  z-index: 100;
  top: 6px; right: 6px;
  padding: 1px 6px;
  border: none;
  border-radius: 100px;
  background: #fddf00;
  color: #17181D;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.02em;
  cursor: pointer;
}
.mapstyle-card .scale-lock-badge:hover { background: #fff; }
.current-style { border: 2px solid #fddf00; }
.cannot-print { opacity: 0.5; }

/* ── More / Legend sub-views ── */
.sub-view { min-height: 100%; }

.more-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #2e2e2e;
  cursor: pointer;
}
.more-option:last-child { border-bottom: none; }
.more-option:hover:not(.more-option--disabled) { opacity: 0.82; }
.more-option--disabled { opacity: 0.4; cursor: default; }

.more-option-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #2d2d2d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ccc;
}
.more-option-icon--featured { background: #fddf00; color: #17181d; }
.more-option-text { flex: 1; line-height: 1.4; }
.more-chevron { color: #555; flex-shrink: 0; }

@media (max-width: 475px) {
  .mapstyle-card { width: 108px; margin-right: 0.7em; }
}
</style>
